import { Button, Card, Divider, FileInput, Grid, Group, Input, Select, Switch, Text, TextInput, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPono } from '../../api/poList';
import { getAllocationList } from '../../api/styleList';
import NavBar from '../../components/NavBar/NavBar';
import HashPrint from '../../components/Print/HashPrint';
import PoLabel from '../../components/Print/PoLabel';
import PrintStoreLabels from '../../components/Print/PrintStorLabels';
import { URL_PREFIX } from '../../constants/stores';
import { useDebounce } from '../../Hooks/useDebounce';
import { AllocationData } from '../../interface/Allocation';
import { PoNoWithStatus } from '../../interface/AllocationInvoice';
import { sampleBox } from '../../PDFs/sampleBox';
import { sampleProduct } from '../../PDFs/sampleProduct';
import { capitalizeFirstLetter, getImageBase64 } from '../../Utils/utils';
import '../Global.css';
import classes from './PrintLabel.module.css';

export const STORE_CODE_MAP = {
    STEELES: 'ST',
    WEB: 'WEB',
    SC_SUPERSTORE: 'SCS',
    BRAMPTON: 'BR',
    SVP_NM: 'NM',
    VAUGHAN: 'V',
    SVP_QW: 'QW',
    ORFUS: 'DO',
    KINGSTON: 'KING',
    KITCHENER: 'KTCH',
    WHITBY: 'WHIT',
    NIAGARA: 'NF',
    ALLISTON: 'AL',
    BARRIE: 'BARRIE',
    BURLINGTON: 'BUR',
    PICKERING_OUTLET: 'PC-O',
    OPM_Aurora: 'OPM-AUR',
    OPM_Online: 'OPM-ONLINE',
} as const;

export const getStoreCode = (store: string): string | undefined => {
    return STORE_CODE_MAP[store as keyof typeof STORE_CODE_MAP];
};
const PrintLabel = () => {
    const initialAllocation = {
        style_id: 0,
        style_no: '',
        po_no: '',
        style_receivedQty: [],
        supplier_name: '',
    }
    const [poNoList, setPoNoList] = useState<any[]>([]);
    const [styleList, setStyleList] = useState<any[]>([]);
    const [allocation, setAllocation] = useState<AllocationData>(initialAllocation);
    const [allocationData, setAllocationData] = useState<any[]>([]);
    const [po, setPo] = useState<{ poNo: string, poId: string }>({ poNo: '', poId: '' });
    const [infoData, setInfoData] = useState<any>({ boxQty: 1 });
    const poSearch = useDebounce(po.poNo, 700);
    const token = useSelector((state: any) => state.user.sessionId);
    const theme = useMantineTheme();
    const isMobile = useMediaQuery("(min-width: 0) and (max-width: 550px)") || false;
    const isTablet = useMediaQuery("(min-width: 551px) and (max-width: 1198px)") || false;
    const isDesktop = useMediaQuery("(min-width: 1199px)") || false;
    const [manual, setManual] = useState<boolean>(false);
    const [single, setSingle] = useState<boolean>(false);
    const [imageData, setImageData] = useState<string>('');
    const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
    const [manualsizeQty, setManualSizeQty] = useState<string>('');
    const [itemType, setItemType] = useState<'clothing' | 'footwear'>('clothing');
    const [manualOverstock, setManualOverstock] = useState<number>();
    const [file, setFile] = useState<File | undefined>(undefined);
    const [boxQty, setBoxQty] = useState<number>();
    const [csvData, setCsvData] = useState<any[]>([]);

    const getPo = async () => {
        const poList = await getPono(token, po?.poNo);
        if (poList) {
            setPoNoList(poList?.data?.map((item: PoNoWithStatus) => {
                return {
                    label: item.poNo,    // Use `item.poNo` for the label
                    value: item.poId!.toString(),  // Convert `poId` to string for the value
                };
            }))
        }
    }

    useEffect(() => {
        getPo();
    }, [poSearch]);


    const handleStyleChange = async (value: string | null) => {
        if (value) {
            setAllocation(allocationData.find((item: any) => item.style_id === parseInt(value)))
            let img = allocationData.find((item: any) => item.style_id === parseInt(value)).image;
            let image = await getImageBase64(`${URL_PREFIX}/uploads/${img}`);
            setImageData(image as string);
        }
    }

    const handlePoNoChange = async (value: string | null) => {
        if (value) {
            setPo({ poId: value?.toString() || '', poNo: poNoList.find((item: any) => item.value === value).label });
            const allocations = await getAllocationList(token, parseInt(value));
            setAllocationData(allocations);
            if (allocations) {
                setStyleList(allocations.map((item: any) => {
                    return {
                        label: item.style_no,
                        value: item.style_id.toString()
                    }
                }))
            }
        }
    }

    const handlePrint = () => {
        if (!infoData.store || !infoData.attn || !po.poNo) {
            alert('Please enter all the required information.');
            return;
        }
        sampleBox(
            po.poNo,
            allocationData[0]?.supplierName,
            infoData.store,
            infoData.returnStore,
            infoData.attn,
            Number(infoData.boxQty)
        );

    }

    const handleProductsPrint = () => {
        if (!allocationData || allocationData.length === 0) {
            alert('No products available to print. Please select a PO number.');
            return;
        }

        // modify the data structure to fit the sampleProduct function { style_no: string, cost: number, stores: string[] }[]
        const formattedData = allocationData.sort((a: any, b: any) => new Date(a.style_updatedAt).getTime() - new Date(b.style_updatedAt).getTime())
            .map((item: any) => ({
                style_no: item.style_no,
                cost: Number(item.cost),
                msrp: Number(item.msrp),
                description: capitalizeFirstLetter(item.description),
                totalQtyReceived: item.receivedQty.reduce((a: number, b: { quantity: number }) => a + b.quantity, 0),
                isOverstock: item.overstockPerSize.reduce((a: number, b: number) => a + b, 0) === 0 ? false : true,
                sizes: item.receivedQty.map((rcvQty: { size: string, quantity: number }) => rcvQty.size),
                stores: item.storeName.map((store: string) => getStoreCode(store.toUpperCase().replace('-', ' ').replace(' ', '_')) || []) // Convert store names to uppercase and replace space with underscore
            }));
        sampleProduct(formattedData);
    }

    const normalizeCsvRow = (row: Record<string, any>) => {
        return Object.entries(row).reduce<Record<string, any>>(
            (acc, [key, value]) => {
                acc[key.trim().toUpperCase()] = value;
                return acc;
            },
            {}
        );
    };

    const getSizesFromSizing = (
        sizing?: string
    ): string[] => {
        switch (
        sizing
            ?.trim()
            .toUpperCase()
        ) {
            case 'ONE SIZE':
                return ['a'];

            case 'LIMITED':
                return ['a', 'b'];

            case 'GOOD':
                return ['a', 'b', 'c', 'd', 'e'];

            case 'OVERSIZE':
                return ['1X', '2X', '3X'];
            default:
                return [];
        }
    };

    const handleCsvProductsPrint = () => {
        if (!csvData || csvData.length === 0) {
            alert('No products available to print. Please import a CSV file.');
            return;
        }

        /*
         * Normalize all CSV column names so column matching
         * is not case sensitive.
         */
        const normalizedCsvData = csvData.map((item) =>
            normalizeCsvRow(item)
        );

        /*
         * Required CSV columns.
         */
        const requiredColumns = [
            'STYLE',
            'DESCRIPTION',
            'COST',
            'MSRP',
            'TOTAL QTY',
            'SIZING',
        ];

        /*
         * Check columns using the first row because every parsed
         * CSV row should have the same headers.
         */
        const availableColumns = Object.keys(
            normalizedCsvData[0]
        );

        const missingColumns = requiredColumns.filter(
            (column) =>
                !availableColumns.includes(column)
        );

        if (missingColumns.length > 0) {
            alert(
                `Invalid CSV file. Missing column${missingColumns.length > 1 ? 's' : ''}: ${missingColumns.join(', ')}`
            );

            return;
        }

        const formattedData =
            normalizedCsvData.map((item) => ({
                style_no:
                    String(
                        item['STYLE'] ?? ''
                    ).trim(),

                cost:
                    Number(
                        item['COST']
                    ) || 0,

                msrp:
                    Number(
                        item['MSRP']
                    ) || 0,

                description:
                    capitalizeFirstLetter(
                        String(
                            item['DESCRIPTION'] ?? ''
                        ).trim()
                    ),

                totalQtyReceived:
                    Number(
                        item['TOTAL QTY']
                    ) || 0,

                isOverstock:
                    false,

                sizes:
                    getSizesFromSizing(
                        String(
                            item['SIZING'] ?? ''
                        )
                    ),

                stores:
                    [],
            }));

        sampleProduct(
            formattedData
        );
    };

    const handleImageUrlChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const url = e.target.value;
        const response = await fetch(url);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = () => {
            setImageData(reader.result as string);
            setFile(new File([blob], 'image.jpg', { type: blob.type }));
        };
        reader.readAsDataURL(blob);
    };

    const storeAttnMap: Record<string, string> = {
        'WEB': 'SARINA',
        'WEB ONLY': 'SARINA',
        'STEELES': 'MIKE',
        'STEELES ONLY': 'MIKE'
    };

    const handleStoreChange = (value: string | null) => {
        if (!value) return;

        const attn = storeAttnMap[value] || 'MIKE'; // default to MIKE if not in map

        setInfoData((prev: any) => ({
            ...prev,
            store: value,
            attn
        }));
    };


    const handleImportCsv = async (file: File | null) => {
        if (!file) {
            return;
        }

        const isCsv =
            file.type === "text/csv" ||
            file.name.toLowerCase().endsWith(".csv");

        if (!isCsv) {
            alert("Please select a valid CSV file.");
            setCsvData([]);
            return;
        }

        try {
            const text = await file.text();

            if (!text.trim()) {
                alert("CSV file is empty.");
                setCsvData([]);
                return;
            }

            const lines = text
                .split(/\r?\n/)
                .filter((line) => line.trim() !== "");

            if (lines.length < 2) {
                alert("CSV file does not contain any product data.");
                setCsvData([]);
                return;
            }

            const parseCsvLine = (line: string) => {
                const values: string[] = [];
                let current = "";
                let insideQuotes = false;

                for (let i = 0; i < line.length; i++) {
                    const char = line[i];

                    if (char === '"') {
                        if (
                            insideQuotes &&
                            line[i + 1] === '"'
                        ) {
                            current += '"';
                            i++;
                        } else {
                            insideQuotes = !insideQuotes;
                        }
                    } else if (
                        char === "," &&
                        !insideQuotes
                    ) {
                        values.push(current.trim());
                        current = "";
                    } else {
                        current += char;
                    }
                }

                values.push(current.trim());

                return values;
            };

            const headers = parseCsvLine(lines[0]).map(
                (header) =>
                    header
                        .replace(/^"|"$/g, "")
                        .trim()
            );

            if (!headers.length) {
                alert("CSV file does not contain valid headers.");
                setCsvData([]);
                return;
            }

            const parsedData = lines
                .slice(1)
                .map((line) => {
                    const values =
                        parseCsvLine(line);

                    const row: Record<
                        string,
                        string
                    > = {};

                    headers.forEach(
                        (
                            header,
                            index
                        ) => {
                            row[header] =
                                values[index]
                                    ?.replace(
                                        /^"|"$/g,
                                        ""
                                    )
                                    .trim() ||
                                "";
                        }
                    );

                    return row;
                })
                .filter((row) =>
                    Object.values(row).some(
                        (value) =>
                            value !== ""
                    )
                );

            if (!parsedData.length) {
                alert("No valid product rows were found in the CSV file.");
                setCsvData([]);
                return;
            }

            setCsvData(parsedData);

            console.log(
                "CSV Data:",
                parsedData
            );
        } catch (error) {
            console.error(
                "Error reading CSV file:",
                error
            );

            setCsvData([]);

            alert(
                "Unable to read the CSV file."
            );
        }
    };
    console.log('Csv: ', csvData);
    return (
        <div>
            <NavBar />
            <div className={'main'}>
                <h2 className={'title'}>Print Label</h2>
                <div style={{ width: '100%', marginBottom: '50px' }}>
                    <Grid>
                        <Grid.Col span={isDesktop ? 6 : 12}>
                            <Grid>
                                <Grid.Col span={12}>
                                    <div className={classes.gridRowDiv} >
                                        <Select
                                            data={poNoList}
                                            label='Po No'
                                            placeholder="Select Po No"
                                            style={{ flexGrow: 1 }}
                                            className={classes.selectDiv}
                                            searchable
                                            clearable
                                            disabled={manual}
                                            onChange={handlePoNoChange}
                                            onSearchChange={(e) => setPo({ ...po, poNo: e.toUpperCase() })}
                                            onClear={() => {
                                                setPo({ poId: '', poNo: '' })
                                                setAllocation(initialAllocation);
                                            }}
                                        />
                                    </div>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Select
                                        data={styleList}
                                        label='Style No'
                                        placeholder="Select Style No"
                                        style={{ flexGrow: 1 }}
                                        className={classes.selectDiv}
                                        searchable
                                        clearable
                                        disabled={manual}
                                        onChange={handleStyleChange}
                                        onClear={() => {
                                            setAllocation(initialAllocation);
                                        }}
                                    />
                                </Grid.Col>
                                <Grid.Col span={6} style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                                    <Switch
                                        label='Manual'
                                        checked={manual}
                                        color={theme.colors.gray[9]}
                                        style={{ marginTop: '33px', flexGrow: 1 }}
                                        onChange={() => setManual(!manual)}
                                    />
                                    {!manual && (<Switch
                                        label='Single'
                                        checked={single}
                                        color={theme.colors.gray[9]}
                                        style={{ marginTop: '33px', flexGrow: 1 }}
                                        onChange={() => {
                                            setOrientation(single ? orientation : 'portrait');
                                            setSingle(!single);
                                        }}
                                    />)}
                                </Grid.Col>
                                <Grid.Col span={12}>
                                    <Grid>
                                        <Grid.Col span={6}>
                                            <Input
                                                style={{ flexGrow: 1 }}
                                                type='file'
                                                accept="image/*"
                                                onChange={(e) => {
                                                    if (e.target.files) {
                                                        const file = e.target.files[0];
                                                        const reader = new FileReader();
                                                        reader.onload = () => {
                                                            if (reader.result) {
                                                                setImageData(reader.result.toString());
                                                                setFile(file);
                                                            }
                                                        }
                                                        reader.readAsDataURL(file);
                                                    }
                                                }}
                                            />
                                        </Grid.Col><Grid.Col span={6}>
                                            <TextInput
                                                style={{ flexGrow: 1 }}
                                                placeholder="Enter image URL"
                                                onChange={handleImageUrlChange}
                                            />
                                        </Grid.Col><Grid.Col span={6}>
                                            <Select
                                                style={{ flexGrow: 1 }}
                                                data={['portrait', 'landscape']}
                                                defaultValue={'portrait'}
                                                value={orientation}
                                                disabled={single}
                                                onChange={(e) => setOrientation(e as 'portrait' | 'landscape')}
                                                placeholder='Select orientation'
                                            />
                                        </Grid.Col><Grid.Col span={6}>
                                            <Select
                                                // style={{ flexGrow: 1 }}
                                                data={[{ label: 'Clothing', value: 'clothing' }, { label: 'Footwear/Accessories', value: 'footwear' }]}
                                                defaultValue={'clothing'}
                                                onChange={(e) => setItemType(e as 'clothing' | 'footwear')}
                                                placeholder='Select orientation'
                                            />
                                        </Grid.Col><Grid.Col span={6} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            {!manual && allocation && allocation.sizeQuantities && allocation.overstockPerSize && allocation.totalAllocationPerSize && (<PrintStoreLabels props={{
                                                allocationData: {
                                                    storeName: allocation.storeName || [],
                                                    sizeQuantities: allocation.sizeQuantities,
                                                }, styleData: {
                                                    color: allocation?.color || '',
                                                    cost: allocation?.cost || 0,
                                                    msrp: allocation?.msrp || 0,
                                                    description: allocation?.description || '',
                                                    supplier_name: allocation?.supplier_name || '',
                                                    style_no: allocation?.style_no || '',
                                                    binlocation: allocation?.binlocation || ''
                                                }, orientation, manualsizeQty, imageData,
                                                storeTotals: allocation?.sizeQuantities.map((item: any) => {
                                                    return item.reduce((a: number, b: { size: string, quantity: number }) => a + b.quantity, 0);
                                                }),
                                                overstockTotal: allocation?.overstockPerSize.reduce((a: number, b: number) => a + b, 0),
                                                styleNo: allocation.style_no || '',
                                                itemType,
                                                manualOverstock,
                                                single,
                                                totalAllocation: allocation.totalAllocationPerSize.reduce((a: number, b: number) => a + b, 0),
                                                file,
                                                boxQty,
                                                btnLabel: 'Store Label'
                                            }} />)}
                                        </Grid.Col><Grid.Col span={6} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            {(<PrintStoreLabels props={{
                                                allocationData: {
                                                    storeName: allocation?.storeName || [],
                                                    sizeQuantities: allocation?.sizeQuantities || [],
                                                }, styleData: {
                                                    color: allocation?.color || '',
                                                    cost: allocation?.cost || 0,
                                                    msrp: allocation?.msrp || 0,
                                                    description: allocation?.description || '',
                                                    supplier_name: allocation?.style_supplier_name || '',
                                                    style_no: allocation?.style_no || '',
                                                    binlocation: allocation?.binlocation || ''
                                                }, orientation, manualsizeQty, imageData,
                                                storeTotals: allocation?.sizeQuantities?.map((item: any) => {
                                                    return item.reduce((a: number, b: { size: string, quantity: number }) => a + b.quantity, 0);
                                                }) || [],
                                                overstockTotal: allocation?.overstockPerSize?.reduce((a: number, b: number) => a + b, 0) || 0,
                                                styleNo: allocation?.style_no || '',
                                                manualOverstock,
                                                itemType,
                                                totalAllocation: allocation?.totalAllocationPerSize?.reduce((a: number, b: number) => a + b, 0) || 0,
                                                file
                                            }} />)}
                                        </Grid.Col>
                                    </Grid>
                                </Grid.Col>
                                <Grid.Col className={classes.storeSection} span={12}>
                                    <Grid>
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label='Style No'
                                                placeholder='Style No'
                                                value={allocation?.style_no}
                                                style={{ flexGrow: 1 }}
                                                onChange={(e) => setAllocation({ ...allocation, style_no: e.target.value.toUpperCase() })}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label='Supplier Name'
                                                placeholder='Supplier Name'
                                                value={allocation?.style_supplier_name || ''}
                                                style={{ flexGrow: 1 }}
                                                onChange={(e) => setAllocation({ ...allocation, style_supplier_name: e.target.value.toUpperCase() })}
                                            />
                                        </Grid.Col><Grid.Col span={6}>
                                            <TextInput
                                                label='Color'
                                                placeholder='Color'
                                                value={allocation?.color || ''}
                                                style={{ flexGrow: 1 }}
                                                onChange={(e) => setAllocation({ ...allocation, color: e.target.value.toUpperCase() })}
                                            />
                                        </Grid.Col><Grid.Col span={6}>
                                            <TextInput
                                                label='Cost'
                                                placeholder='cost'
                                                value={allocation?.cost}
                                                style={{ flexGrow: 1 }}
                                                onChange={(e) => {
                                                    const parsedValue = parseFloat(e.target.value);
                                                    setAllocation({ ...allocation, cost: isNaN(parsedValue) ? 0 : parsedValue });
                                                }} />
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label='Description'
                                                placeholder='description'
                                                value={allocation?.description}
                                                style={{ flexGrow: 1 }}
                                                onChange={(e) => setAllocation({ ...allocation, description: e.target.value.toUpperCase() })}
                                            />
                                        </Grid.Col><Grid.Col span={6}>
                                            <TextInput
                                                type='number'
                                                label='MSRP'
                                                placeholder='msrp'
                                                value={allocation?.msrp}
                                                style={{ flexGrow: 1 }}
                                                onChange={(e) => {
                                                    const parsedValue = parseFloat(e.target.value);
                                                    setAllocation({ ...allocation, msrp: isNaN(parsedValue) ? 0 : parsedValue });
                                                }} />
                                        </Grid.Col><Grid.Col span={6}>
                                            <TextInput
                                                label='Size/Qty'
                                                placeholder='Size/Qty'
                                                // value={allocation?.style_no}
                                                style={{ flexGrow: 1 }}
                                                onChange={(e) => setManualSizeQty(e.target.value.toUpperCase())}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <TextInput
                                                label='Packed Qty per box'
                                                placeholder='Qty'
                                                type='number'
                                                // value={allocation?.style_no}
                                                style={{ flexGrow: 1 }}
                                                onChange={(e) => setBoxQty(parseInt(e.target.value))}
                                            />
                                        </Grid.Col>
                                        {(manual &&
                                            <Grid.Col span={6}> <TextInput
                                                type='number'
                                                className='text-input'
                                                label="STEELES + OVERSTOCK"
                                                value={manualOverstock}
                                                ta={"center"}
                                                onChange={(e) => setManualOverstock(parseInt(e.target.value))}
                                            /> </Grid.Col>)}
                                    </Grid>
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                        <Grid.Col className={classes.storeSection} span={isMobile ? 12 : isTablet ? 12 : 6}>
                            <div style={{ height: '65vh', overflow: 'auto', scrollbarWidth: 'none', border: '1px solid #ccc', padding: '10px' }}>
                                <div style={{ width: '100%', marginBottom: '10px' }}>
                                    <Grid>
                                        <Grid.Col span={12}>
                                            <Text style={{ width: '100%', textAlign: 'center', fontWeight: 'bold', fontSize: '16px', color: '#656565', borderBottom: '1px solid #b3b3b3ff', paddingBottom: '5px' }}>Sample box label</Text>
                                        </Grid.Col>
                                        <Grid.Col span={12} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                            {/* <Text className={classes.textWithInput}>Po No:</Text> */}
                                            <Text style={{ width: '100%', textAlign: 'center', fontSize: '20px', color: '#656565' }}>{po.poNo}</Text>
                                        </Grid.Col>
                                    </Grid>
                                </div>
                                <div style={{ width: '100%' }}>
                                    <Grid>
                                        <Grid.Col span={3}>
                                            <div className={classes.gridRowDiv} >
                                                <Select
                                                    data={[{ label: 'STEELES', value: 'ST' }, { label: 'STEELES ONLY', value: 'ST ONLY' }, { label: 'WEB ONLY', value: 'WEB ONLY' }, { label: 'WEB', value: 'WEB' }]}
                                                    label='Store'
                                                    placeholder="Select store"
                                                    style={{ flexGrow: 1 }}
                                                    className={classes.selectDiv}
                                                    searchable
                                                    clearable
                                                    onChange={handleStoreChange}
                                                />
                                            </div>
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <div className={classes.gridRowDiv} >
                                                <TextInput
                                                    value={infoData?.attn}
                                                    placeholder='Attention'
                                                    label='Attention'
                                                    style={{ flexGrow: 1 }}
                                                    onChange={(e) => setInfoData({ ...infoData, attn: e.target.value })}
                                                />
                                            </div>
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <div className={classes.gridRowDiv} >
                                                <TextInput
                                                    value={infoData?.boxQty}
                                                    type='number'
                                                    defaultValue={1}
                                                    placeholder='Box Quantity'
                                                    label='Box Quantity'
                                                    style={{ flexGrow: 1 }}
                                                    onChange={(e) => setInfoData({ ...infoData, boxQty: e.target.value })}
                                                />
                                            </div>
                                        </Grid.Col>
                                        <Grid.Col span={3}>
                                            <div className={classes.gridRowDiv} >
                                                <Select
                                                    data={[{ label: 'STEELES', value: 'STEELES' }, { label: 'WEB', value: 'WEB' }, { label: 'DC-RICHMOND', value: 'DC-RICHMOND' }]}
                                                    label='Return Store'
                                                    placeholder="Select return store"
                                                    style={{ flexGrow: 1 }}
                                                    className={classes.selectDiv}
                                                    searchable
                                                    clearable
                                                    onChange={(value) => setInfoData({ ...infoData, returnStore: value })}
                                                />
                                            </div>
                                        </Grid.Col>
                                        <Divider my={'lg'} />
                                        <Grid.Col span={12} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                                            <Button style={{ width: '160px' }} onClick={handlePrint}>Box Label</Button>
                                            <Button style={{ width: '160px' }} onClick={handleProductsPrint}>Products Label</Button>
                                        </Grid.Col>
                                        <Grid.Col span={12}>
                                            <Card
                                                withBorder
                                                radius="md"
                                                p="sm"
                                                style={{
                                                    maxWidth: '560px',
                                                    margin: '0 auto',
                                                    width: '100%',
                                                }}
                                            >
                                                <Group
                                                    justify="space-between"
                                                    align="flex-end"
                                                    wrap="nowrap"
                                                >
                                                    <div style={{ minWidth: '90px' }}>
                                                        <Text fw={700} size="sm">
                                                            CSV Labels
                                                        </Text>

                                                        <Text size="xs" c="dimmed">
                                                            Import product data
                                                        </Text>
                                                    </div>

                                                    <FileInput
                                                        multiple={false}
                                                        onChange={handleImportCsv}
                                                        accept=".csv,text/csv"
                                                        placeholder="Choose CSV file"
                                                        style={{
                                                            flex: 1,
                                                            minWidth: '180px',
                                                        }}
                                                    />

                                                    <Button
                                                        style={{
                                                            minWidth: '150px',
                                                        }}
                                                        disabled={csvData?.length === 0 || !csvData}
                                                        onClick={handleCsvProductsPrint}
                                                    >
                                                        Print Labels
                                                    </Button>
                                                </Group>
                                            </Card>
                                        </Grid.Col>
                                    </Grid>
                                </div>
                            </div>
                        </Grid.Col>
                    </Grid>
                    <Grid style={{ marginTop: '30px' }} >
                    </Grid>
                </div>
            </div >
            <div className={"footerDiv"}>
                <div>{po.poNo && <PoLabel poNo={po.poNo} />}</div>
                <div></div>
                {allocationData.length > 0 && (<HashPrint allocationData={allocationData} />)}
            </div>
        </div >
    )
}


export default PrintLabel;